import { useSyncExternalStore } from 'react';
import moment from 'moment-timezone';

// Whether a reminder rings is decided once per page load, not by the clock: one already past
// when this page first sees it rings straight away; one still upcoming waits for the backend's
// `reminder_due` socket message, which only comes if its "Remind me only if…" condition held.
// Keyed `${workOrderId}-${remindAtMillis}`, so a snooze (a new time) waits again.
const ringingKeys = new Set<string>();
// Waiting key → its reminder time, so a reconnect can tell which ones are now overdue.
const waitingRemindAts = new Map<string, number>();
// Rang, then silenced by a due count of 0: seen, so they don't ring again on first-sight rules.
const silencedKeys = new Set<string>();
const listeners = new Set<() => void>();

const reminderKey = (workOrderId: number | string, remindAt: moment.Moment): string =>
	`${workOrderId}-${remindAt.valueOf()}`;

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};
const notify = () => listeners.forEach((listener) => listener());

// The backend fired this reminder: its socket message arrived.
export const markReminderRinging = (
	workOrderId: number | string,
	remindAt: moment.Moment | null
): void => {
	if (!remindAt) return;
	const key = reminderKey(workOrderId, remindAt);
	if (ringingKeys.has(key)) return;
	waitingRemindAts.delete(key);
	silencedKeys.delete(key);
	ringingKeys.add(key);
	notify();
};

// After a socket reconnect, messages sent while it was down are lost, so a reminder whose time
// passed meanwhile is treated as it would be on a fresh load: overdue, so ringing.
// No reminders are due (the backend's count is 0): every bell stops. A reminder that comes due later
// rings again through its socket message.
export const stopAllRinging = (): void => {
	if (ringingKeys.size === 0) return;
	ringingKeys.forEach((key) => silencedKeys.add(key));
	ringingKeys.clear();
	notify();
};

export const ringOverdueReminders = (): void => {
	const nowMillis = Date.now();
	let changed = false;
	waitingRemindAts.forEach((remindAtMillis, key) => {
		if (remindAtMillis > nowMillis) return;
		waitingRemindAts.delete(key);
		ringingKeys.add(key);
		changed = true;
	});
	if (changed) notify();
};

const useReminderRinging = (
	workOrderId: number | string | null,
	remindAt: moment.Moment | null
): boolean => {
	const key = workOrderId && remindAt ? reminderKey(workOrderId, remindAt) : null;
	// First sight decides; recording it is idempotent, so doing it during render is safe.
	if (key && !ringingKeys.has(key) && !waitingRemindAts.has(key) && !silencedKeys.has(key)) {
		if (remindAt.isAfter(moment())) waitingRemindAts.set(key, remindAt.valueOf());
		else ringingKeys.add(key);
	}
	return useSyncExternalStore(subscribe, () => !!key && ringingKeys.has(key));
};

export default useReminderRinging;
